/**
 * The dialog before `Publish v15` — the redesign's modal M with its diff (§20, §44.8).
 *
 * `Publish` used to be one click that wrote a whole version every install reads
 * at its next check, with nothing on screen saying who it landed on. The
 * document is right — the rows are typed and a 422 is unreachable from the page
 * — and that was never the risk. The risk is a **lock**: `enforced` can freeze
 * every setting on every machine in the company at once, and it is the
 * one act here that takes something away from somebody else without their
 * asking.
 *
 * So the dialog shows the change in the language of the rows — a section's
 * kicker, then per setting its label and key with the old value struck in red
 * and the new one in green — and then **What this takes away**, a line per
 * workspace a lock overrules. The way out, *v14 stays restorable from the
 * history*, sits in the foot: it is what makes the button pressable rather than
 * frightening. The consequence under a row is grey, not amber: it is what the
 * change does, not an alert (rule 15).
 *
 * ## Past a handful it sorts
 *
 * At ~70px a row, 23 changes is a long scroll to reach a button, which is a
 * dialog people press anyway. So past {@link LISTABLE}:
 *
 * - **What this takes away comes first**, in a hairline frame, because it is the
 *   only part an administrator has not already got in their head — it is about
 *   somebody else's machine.
 * - A one-line summary, and chips that filter by kind (All, Values, Locks,
 *   Lists). The consequence is said once per kind instead of under each row.
 * - Each section is a sticky header that folds, only the first one open: they
 *   typed the rest ten minutes ago and need to confirm it is there, not re-read
 *   it.
 */
import { useState } from 'react';
import type React from 'react';
import { color, font, radius } from '../../theme/tokens';
import { Modal } from '../../components/Modal';
import { ChevronDown } from '../../components/icons';
import { Kicker } from '../../components/primitives';
import { since } from '../../lib/format';
import type { OrgSettingsReach } from '../../lib/types';
import { reachOf, takenAway, type SettingChange } from './settings-diff';

/** Up to this many changes are simply listed; past it the dialog sorts. */
const LISTABLE = 6;

type Sort = 'all' | 'values' | 'locks' | 'lists';

/** A list the organization edits — hosts, the library, the models a run may use. */
const isList = (change: SettingChange): boolean => /^policy\.(egress|extensions|models)/.test(change.key);
const isLock = (change: SettingChange): boolean => change.kind === 'lock' || change.kind === 'unlock';
const sortOf = (change: SettingChange): Exclude<Sort, 'all'> =>
  isLock(change) ? 'locks' : isList(change) ? 'lists' : 'values';

const plural = (count: number, one: string, many = `${one}s`): string => `${count} ${count === 1 ? one : many}`;

/** What a row does, said under it while the dialog still lists. */
function consequence(change: SettingChange, reach: OrgSettingsReach | null): string {
  if (change.kind === 'lock') return 'A member can no longer change it on their own machine.';
  if (change.key === 'policy.egress.blockedDomains' && change.takes)
    return 'Refused on every member’s machine from their next check.';
  return reachOf(change, reach).text;
}

/** The consequence said once per kind, past {@link LISTABLE}. */
function perKind(reach: OrgSettingsReach | null): string {
  const installs = reach?.installs;
  const read = installs ? `${plural(installs, 'install')} ${installs === 1 ? 'reads' : 'read'} it` : 'installs read it';
  return `Every value change: ${read} at their next check. Every lock: a member can no longer change it on their own machine.`;
}

export function PublishSettings({
  version,
  changes,
  reach,
  publishedAt,
  busy,
  problem,
  onConfirm,
  onCancel
}: {
  /** The version about to be written — the current one plus one. */
  version: number;
  changes: SettingChange[];
  /** `null` while the reach is still being read, or where it could not be. */
  reach: OrgSettingsReach | null;
  publishedAt: string | null;
  busy?: boolean;
  problem?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [sort, setSort] = useState<Sort>('all');
  const sorted = changes.length > LISTABLE;
  const taken = changes.flatMap((change) => takenAway(change, reach));
  // A change that takes something away (blocks a host, stops allowing one,
  // narrows an agent's models) is never hidden by a chip nor folded away
  // (0373/org-policy-lists-not-rows): it is about somebody else's machine.
  const shown = sorted && sort !== 'all' ? changes.filter((change) => sortOf(change) === sort || change.takes) : changes;
  const sections = [...new Set(shown.map((change) => change.family))];
  const locks = changes.filter((change) => change.kind === 'lock').length;
  const summary = [
    `${plural(changes.length, 'change')} in ${plural(new Set(changes.map((change) => change.family)).size, 'section')}`,
    locks ? plural(locks, 'lock') : '',
    taken.length ? `${plural(taken.length, 'workspace value')} taken away` : ''
  ]
    .filter(Boolean)
    .join(' · ');

  const away = taken.length > 0 && (
    <section
      style={
        sorted
          ? { padding: '12px 14px', border: `1px solid ${color.line}`, borderRadius: radius.md, marginBottom: 18 }
          : { marginTop: 18 }
      }
    >
      <div style={{ fontFamily: font.mono, fontSize: 10.5, letterSpacing: '.14em', textTransform: 'uppercase', color: color.warnText }}>
        What this takes away
      </div>
      <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
        {taken.map((line) => (
          <li key={line} style={{ fontSize: 13, lineHeight: 1.5, color: color.inkMuted }}>
            {line}
          </li>
        ))}
      </ul>
    </section>
  );

  return (
    <Modal
      width={640}
      title={`Publish version ${version}`}
      meta={publishedAt ? `since v${version - 1} · ${since(publishedAt)}` : `v${version - 1}`}
      lede="Publishing sends every section, not only this one."
      confirm={`Publish v${version}`}
      busy={busy}
      problem={problem}
      foot={
        <span className="publish-foot">
          Installs read it at their next check. v{version - 1} stays restorable from the history.
        </span>
      }
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <style>{'@media (max-width: 640px) { .publish-foot { display: none; } }'}</style>
      <div
        className="nsb"
        style={{
          maxHeight: 'min(52vh, 480px)',
          overflowY: 'auto',
          margin: '16px -22px 0',
          padding: '0 22px 4px',
          borderTop: `1px solid ${color.line}`,
          borderBottom: `1px solid ${color.line}`
        }}
      >
        {changes.length === 0 ? (
          <p style={{ margin: '16px 0', fontSize: 13, lineHeight: 1.6, color: color.inkSubtle }}>
            Nothing has moved since v{version - 1}. Publishing writes a version that is identical to it, which is a way of
            saying so in the history and nothing more.
          </p>
        ) : sorted ? (
          <div style={{ paddingTop: 16 }}>
            {away}
            <p style={{ margin: 0, fontFamily: font.mono, fontSize: 12, color: color.inkMuted }}>{summary}</p>
            <div role="group" aria-label="Show" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
              {(['all', 'values', 'locks', 'lists'] as const).map((one) => {
                const count = one === 'all' ? changes.length : changes.filter((change) => sortOf(change) === one).length;
                return (
                  <FilterChip key={one} on={sort === one} count={count} onClick={() => setSort(one)}>
                    {one === 'all' ? 'All' : one[0].toUpperCase() + one.slice(1)}
                  </FilterChip>
                );
              })}
            </div>
            <p style={{ margin: '10px 0 4px', fontSize: 13, lineHeight: 1.5, color: color.inkMuted }}>{perKind(reach)}</p>
            {sections.map((family, index) => (
              <Folding
                key={`${sort}:${family}`}
                family={family}
                changes={shown.filter((change) => change.family === family)}
                open={index === 0 || shown.some((change) => change.family === family && change.takes)}
              />
            ))}
          </div>
        ) : (
          <>
            {sections.map((family) => (
              <section key={family} style={{ marginTop: 16 }}>
                <Kicker>{family}</Kicker>
                {changes
                  .filter((change) => change.family === family)
                  .map((change) => (
                    <Line key={change.key} change={change} said={consequence(change, reach)} />
                  ))}
              </section>
            ))}
            {away}
          </>
        )}
      </div>
    </Modal>
  );
}

/* ── the pieces ─────────────────────────────────────────────────────────── */

/** One setting: its label and key at the left, before → after at the right, its consequence under. */
function Line({ change, said }: { change: SettingChange; said?: string }): React.JSX.Element {
  return (
    <div style={{ padding: '10px 0', borderBottom: `1px solid ${color.hairSoft}` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, minWidth: 0 }}>
        <div style={{ flex: '1 1 40%', minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 500, color: color.ink }}>{change.label}</div>
          <div style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint, overflowWrap: 'anywhere' }}>
            {change.key}
          </div>
        </div>
        <div
          style={{
            flex: '1 1 60%',
            minWidth: 0,
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: '2px 8px',
            fontFamily: font.mono,
            fontSize: 12.5,
            textAlign: 'right'
          }}
        >
          {change.kind === 'lock' && <LockBadge />}
          <span className="al-sr">Before</span>
          <s style={{ color: color.badText, overflowWrap: 'anywhere' }}>{change.was}</s>
          <span aria-hidden="true" style={{ color: color.inkGhost }}>
            →
          </span>
          <span className="al-sr">After</span>
          <span style={{ color: color.okText, fontWeight: 500, overflowWrap: 'anywhere' }}>{change.now}</span>
        </div>
      </div>
      {said && <p style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.5, color: color.inkMuted }}>{said}</p>}
    </div>
  );
}

function LockBadge(): React.JSX.Element {
  return (
    <span
      style={{
        alignSelf: 'center',
        display: 'inline-flex',
        alignItems: 'center',
        height: 20,
        padding: '0 7px',
        borderRadius: 6,
        background: color.hairSoft,
        color: color.inkMuted,
        fontFamily: font.mono,
        fontSize: 10.5,
        letterSpacing: '.16em'
      }}
    >
      LOCK
    </span>
  );
}

/** A filter chip (§5): 28 high, hairline, ink when on. */
function FilterChip({
  on,
  count,
  onClick,
  children
}: {
  on: boolean;
  count: number;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 28,
        padding: '0 12px',
        border: `1px solid ${on ? color.ink : color.line}`,
        borderRadius: 999,
        background: on ? color.ink : color.surface,
        color: on ? '#FFFFFF' : color.ink,
        fontFamily: 'inherit',
        fontSize: 12.5,
        cursor: 'pointer'
      }}
    >
      {children}
      <span style={{ fontFamily: font.mono, fontSize: 11, opacity: 0.7 }}>{count}</span>
    </button>
  );
}

/** A section past {@link LISTABLE}: a sticky header that folds, its rows without their consequence. */
function Folding({
  family,
  changes,
  open: first
}: {
  family: string;
  changes: SettingChange[];
  open: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(first);
  return (
    <section>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          height: 40,
          padding: 0,
          border: 0,
          borderBottom: `1px solid ${color.line}`,
          background: color.surface,
          textAlign: 'left',
          cursor: 'pointer'
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <Kicker>{family}</Kicker>
        </span>
        <span style={{ fontFamily: font.mono, fontSize: 11, color: color.inkFaint }}>{changes.length}</span>
        <span style={{ display: 'flex', color: color.inkGhost }}>
          <ChevronDown open={open} />
        </span>
      </button>
      {open && changes.map((change) => <Line key={change.key} change={change} />)}
    </section>
  );
}
